import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TopBarContext } from '../TopBar';
import { Box, Container, Snackbar, Typography, Paper } from '@mui/material';
import FavoriteButton from '../components/FavoriteButton';
import { Pathology } from '../classes/pathology';
import Progress from '../components/Progress';
import { ErrorDialogContext } from '../components/ErrorDialog';
import { useSystemState, useSystemStateDispatch } from '../SystemState';
import PathologyGraphLoader from '../components/graph/PathologyGraphLoader';
import PathologySections from '../components/PathologySections';

export interface PathologyDetailsProps {
  urlPrefix: string;
}

function PathologyDetails(props: PathologyDetailsProps) {
  const [ favorite, setFavorite ] = React.useState<boolean>(false);
  const [ snackBarState, setSnackBarState ] = React.useState<boolean>(false);
  const [ pathology, setPathology ] = React.useState<Pathology>();
  const [ displayFavBtn, setDisplayFavBtn ] = React.useState<boolean>(false);
  const [ graphDataAvailable, setGraphDataAvailable ] = React.useState<boolean>(true);

  const params = useParams();
  const navigate = useNavigate();
  const state = useSystemState();
  const dispatch = useSystemStateDispatch();

  const topBarCtx = React.useContext(TopBarContext);
  const errorDialogCtx = React.useContext(ErrorDialogContext);

  const toggleFavorite = () => {
    if(pathology) dispatch({ type: 'setFavorite', item: pathology, add: !favorite});
    setFavorite(!favorite);
    setSnackBarState(true);
    setTimeout(() => {
      setSnackBarState(false);
    }, 3000);
  };

  React.useEffect(() => {
    topBarCtx?.setTitle('Dettagli Patologia');
  }, []);

  React.useEffect(() => {
    // scroll to top on page load
    // this is needed because the page is not reloaded when the id in the url changes
    window.scrollTo({ top: 0 });
    setPathology(undefined);
    setGraphDataAvailable(true);

    state.pathologies.then((pathologies) => {
      const pathology = pathologies.getById(params.id ?? '');
      if(pathology) {
        setPathology(pathology);

        // Check if the graph data file exists and contains valid JSON
        fetch(`/data/${pathology.code}.json`)
          .then(response => {
            if (!response.ok) {
              setGraphDataAvailable(false);
              return;
            }
            // Try to parse the JSON to verify it's valid
            return response.json()
              .then(data => {
                // Verify it has the expected structure
                setGraphDataAvailable(data && data.nodes && data.edges);
              })
              .catch(() => {
                // JSON parse error
                setGraphDataAvailable(false);
              });
          })
          .catch(() => {
            setGraphDataAvailable(false);
          });

        state.favorites.then((favorites) => {
          setFavorite(favorites.isFavorite(pathology));
          setDisplayFavBtn(true);
        }).catch((err) => {
          errorDialogCtx?.setContent(err.message);
          errorDialogCtx?.setOpen(true);
        });
      } else {
        navigate('/404');
      }
    }).catch((err) => {
      errorDialogCtx?.setContent(err.message);
      errorDialogCtx?.setOpen(true);
    });
  }, [params.id]);

  if(!pathology) {
    return <Progress inPage />
  }

  return (
    <React.Fragment>
      { displayFavBtn && <FavoriteButton isFavorite={favorite} toggleFavorite={toggleFavorite} /> }
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackBarState}
        message={ favorite ? 'Aggiunto ai preferiti' : 'Rimosso dai preferiti' }
      />
      <Container maxWidth="md">
        <Box sx={{ my: 1 }}>
          <Typography variant="h4" component="h1">{ pathology.title }</Typography>
        </Box>

        {/* Pathology Graph - only shown if data is available */}
        {graphDataAvailable && (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <PathologyGraphLoader jsonPath={`/data/${pathology.code}.json`} />
          </Paper>
        )}
        
        {/* Pathology Header */}
        <Box sx={{ my: 4 }}>
          {/* Pathology Detail Sections */}
          <PathologySections pathology={pathology} />
        </Box>
      </Container>
    </React.Fragment>
  );
}

export default PathologyDetails;
